import { ATTRIBUTES, NO, YES } from '../constants';
import { getAltId } from './RetrofitAttributesMapping';
import { AttributesFilterInputInterface } from '../interfaces/AttributesFilterInputInterface';
import { AttributeNameValuesPairInterface } from '../interfaces/AttributeNameValuesPairInterface';
import { OperationsEnum } from '../enums/OperationsEnum';

export const FAIL_SAFE_MAPPING: [string, boolean][] = [
  ['NULL', false],
  ['Electronic fail-safe', true],
  ['Electrical fail-safe', true],
  ['Fail-Safe', true],
  ['Fail-safe', true],
  ['Yes', true],
  ['Spring return', true],
];

const YES_VALUE = `%%${JSON.stringify(YES)}%%`;
const NO_VALUE = `%%${JSON.stringify(NO)}%%`;
const BELIMO_REGEXP = /Belimo/i;

const positiveValues = FAIL_SAFE_MAPPING.filter(([, v]) => v)
    .map(([value]) => JSON.stringify([value]));

const eqToNull: Partial<AttributeNameValuesPairInterface> = { op: OperationsEnum.eq, values: null };
const noPositive: Partial<AttributeNameValuesPairInterface> =  { op: OperationsEnum.nin, values: positiveValues };
const eqToNo: Partial<AttributeNameValuesPairInterface> = { op: OperationsEnum.eq, values: ['["No"]', '["NO"]', '["no"]'] };
const eqToYes: Partial<AttributeNameValuesPairInterface> = { op: OperationsEnum.eq, values: ['["YES"]', '["Yes"]', '["yes"]'] };

function iterate(filter: AttributesFilterInputInterface): AttributesFilterInputInterface {
    const or = (filter.or || []).map((item) => iterate(item));
    const and = (filter.and || []).map((item) => iterate(item));
    const values = filter.values.reduce((acc, curr) => {
        const [firstValue = null] = curr.values === null ? [null] : curr.values;
        if (curr.name === ATTRIBUTES.COMPANY_COMPETITOR && BELIMO_REGEXP.test(`${firstValue}`)) {
            or.push({ values: [curr] });
            or.push({ values: [{ ...curr, ...eqToNull }] });
            return acc;
        }
        if (curr.name === ATTRIBUTES.INTEGRATED_SWITCH_COMPETITOR) {
            const attrName = { name: ATTRIBUTES.INTEGRATED_SWITCH };
            if (firstValue === NO_VALUE) {
                or.push(({
                    values: [],
                    and: [
                        { values: [{ ...curr, ...eqToNull }] },
                        { values: [{ ...curr, ...eqToNo, ...attrName }] },
                    ],
                }));
                or.push(({
                    values: [],
                    and: [
                        { values: [{ ...curr, ...eqToNull, ...attrName }] },
                        { values: [{ ...curr, ...eqToNo }] },
                    ],
                }));
                or.push({
                    values: [],
                    and: [
                        { values: [{ ...curr, ...eqToNull }] },
                        { values: [{ ...curr, ...eqToNull, ...attrName }] },
                    ],
                });
            } else {
                or.push({ values: [curr] });
                or.push({ values: [{ ...curr, ...attrName }] });
            }
            return acc;
        }
        if (curr.name === ATTRIBUTES.INTEGRATED_SWITCH) {
            if (firstValue === NO_VALUE) {
                or.push(({ values: [{ ...curr, ...eqToNull }] }));
                or.push(({ values: [{ ...curr, ...eqToNo }] }));
            } else {
                or.push({ values: [curr] });
            }
            return acc;
        }
        if (curr.name === ATTRIBUTES.DISPLAY_TYPE) {
            if (firstValue === NO_VALUE) {
                or.push(({ values: [{ ...curr, ...eqToNull }] }));
                or.push(({ values: [{ ...curr, ...eqToNo }] }));
            } else {
                or.push({ values: [curr] });
            }
            return acc;
        }
        if (curr.name === ATTRIBUTES.DISPLAY_TYPE_SAS) {
            if (firstValue === NO_VALUE) {
                or.push(({ values: [{ ...curr, ...eqToNull }] }));
                or.push(({ values: [{ ...curr, ...eqToNo }] }));
            } else {
                or.push({ values: [{ ...curr, ...eqToYes }] });
            }
            return acc;
        }
        if (curr.name === ATTRIBUTES.PRESSURE_DEPENDENT) {
            if (firstValue === NO_VALUE) {
                or.push(({ values: [{ ...curr, ...eqToNull }] }));
                or.push(({ values: [{ ...curr, ...eqToNo }] }));
            } else {
                or.push({ values: [{ ...curr, ...eqToYes }] });
            }
            return acc;
        }
        if (curr.name === ATTRIBUTES.RETROFIT_FAIL_SAFE) {
            // rebuild filter values for fail-safe attr
            // using real values and fail-safe attribute for solution products
            const failSafeName = { name: ATTRIBUTES.FAIL_SAFE };
            if (firstValue === YES_VALUE) {
                const positive = { op: OperationsEnum.in, values: positiveValues };
                or.push({ values: [{ ...curr, ...positive }] });
                or.push({ values: [{ ...curr, ...positive, ...failSafeName }] });
            }
            if (firstValue === NO_VALUE) {
                or.push(({
                    values: [],
                    and: [
                        { values: [{ ...curr, ...eqToNull }] },
                        { values: [{ ...curr, ...noPositive, ...failSafeName }] },
                    ],
                }));
                or.push(({
                    values: [],
                    and: [
                        { values: [{ ...curr, ...eqToNull, ...failSafeName }] },
                        { values: [{ ...curr, ...noPositive }] },
                    ],
                }));
                or.push({
                    values: [],
                    and: [
                        { values: [{ ...curr, ...eqToNull }] },
                        { values: [{ ...curr, ...eqToNull, ...failSafeName }] },
                    ],
                });
            }
            return acc;
        }
        if (curr.name === ATTRIBUTES.FAIL_SAFE) {
            if (firstValue === YES_VALUE) {
                const positive = { op: OperationsEnum.in, values: positiveValues };
                or.push({ values: [{ ...curr, ...positive }] });
            }
            if (firstValue === NO_VALUE) {
                or.push(({ values: [{ ...curr, ...eqToNull }] }));
                or.push(({ values: [{ ...curr, ...noPositive }] }));
            }
            return acc;
        }
        const altId = getAltId(curr.name);
        if (altId !== null) {
            if (curr.values === null) {
                or.push({
                    values: [],
                    and: [
                        { values: [curr] },
                        { values: [{ ...curr, name: altId }] },
                    ],
                });
            } else {
                or.push({ values: [curr] });
                or.push({ values: [{ ...curr, name: altId }] });
            }
            return acc;
        }
        return [...acc, curr];
    }, [] as AttributeNameValuesPairInterface[]);
    return {
        values,
        ...(or.length > 0 ? { or } : {}),
        ...(and.length > 0 ? { and } : {}),
    };
}

export class MapFilterValuesPipe {
    public transform(value: AttributesFilterInputInterface = { values: [] }): AttributesFilterInputInterface {
        return iterate(value);
    }
}
